import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import clsx from 'clsx';
import { ApiError } from '@/lib/api';
import { isAdminRole, useAuthStore } from '@/lib/auth-store';
import {
  acceptCorrectionApi,
  ignoreCorrectionApi,
  listCorrectionsApi,
  rejectCorrectionApi,
  getCorrectionStatsApi,
  type CorrectionListItem,
  type CorrectionStatus,
  type CorrectionsListQuery,
} from '@/lib/corrections-api';
import { getKnowledgeEntryApi } from '@/lib/knowledge-api';
import { Card } from '@/components/ui/card';
import { Modal } from '@/components/ui/modal';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/empty-state';
import { formatDateTime } from '@/lib/format';

// Issue #341：题目纠错 / 优化建议处理台。列表 + 筛选 + 分页 + 详情 + 处理（采纳并修改 /
// 驳回 / 忽略）。处理仅 ADMIN / SUPER_ADMIN；采纳走真实知识库更新（后端复用
// KnowledgeService.update + 审计），绝不前端假状态。

const PRIMARY_BTN =
  'inline-flex items-center gap-2 rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:opacity-60';
const SECONDARY_BTN =
  'inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs text-slate-600 transition hover:bg-slate-50 disabled:opacity-60';
const DANGER_BTN =
  'inline-flex items-center gap-2 rounded-md bg-rose-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-rose-700 disabled:opacity-60';
const INPUT =
  'rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100';

const STATUS_OPTIONS: { value: CorrectionStatus | 'ALL'; label: string }[] = [
  { value: 'ALL', label: '全部' },
  { value: 'PENDING', label: '待处理' },
  { value: 'ACCEPTED', label: '已采纳' },
  { value: 'REJECTED', label: '已驳回' },
  { value: 'IGNORED', label: '已忽略' },
];

const STATUS_META: Record<CorrectionStatus, { label: string; cls: string }> = {
  PENDING: { label: '待处理', cls: 'bg-amber-100 text-amber-700' },
  ACCEPTED: { label: '已采纳', cls: 'bg-emerald-100 text-emerald-700' },
  REJECTED: { label: '已驳回', cls: 'bg-rose-100 text-rose-700' },
  IGNORED: { label: '已忽略', cls: 'bg-slate-200 text-slate-600' },
};

const PAGE_SIZE = 20;

export function CorrectionsPage() {
  const user = useAuthStore((s) => s.user);
  const canHandle = isAdminRole(user?.role);
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<CorrectionStatus | 'ALL'>('PENDING');
  const [pendingKeyword, setPendingKeyword] = useState('');
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<CorrectionListItem | null>(null);

  const listQuery = useQuery({
    queryKey: ['admin', 'corrections', status, keyword, page],
    queryFn: () => {
      const q: CorrectionsListQuery = { page, pageSize: PAGE_SIZE };
      if (status !== 'ALL') q.status = status;
      if (keyword.trim()) q.keyword = keyword.trim();
      return listCorrectionsApi(q);
    },
  });

  const statsQuery = useQuery({
    queryKey: ['admin', 'corrections', 'stats'],
    queryFn: () => getCorrectionStatsApi(),
  });

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['admin', 'corrections'] }),
    ]);
  };

  const rows = listQuery.data?.items ?? [];
  const pagination = listQuery.data?.pagination;

  return (
    <div className="flex flex-col gap-6">
      <header>
        <div className="text-xs text-slate-500">培训中心 / 题目纠错</div>
        <h1 className="mt-1 flex items-center gap-3 text-2xl font-semibold text-slate-900">
          题目纠错 / 优化建议
          {typeof statsQuery.data?.pending === 'number' && statsQuery.data.pending > 0 ? (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
              待处理 {statsQuery.data.pending}
            </span>
          ) : null}
        </h1>
      </header>

      <Card title="纠错处理列表">
        {/* 筛选栏 */}
        <div className="mb-3 flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1 text-xs text-slate-500">
            <span>状态</span>
            <select
              className={INPUT}
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as CorrectionStatus | 'ALL');
                setPage(1);
              }}
              data-testid="correction-status-filter"
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-500">
            <span>关键词（建议 / 题干）</span>
            <div className="flex items-center gap-1">
              <input
                className={clsx(INPUT, 'w-56')}
                value={pendingKeyword}
                placeholder="搜索建议或题干…"
                onChange={(e) => setPendingKeyword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setKeyword(pendingKeyword);
                    setPage(1);
                  }
                }}
                data-testid="correction-keyword"
              />
              <button
                type="button"
                className={SECONDARY_BTN}
                onClick={() => {
                  setKeyword(pendingKeyword);
                  setPage(1);
                }}
              >
                <Search className="h-3.5 w-3.5" />
                查询
              </button>
            </div>
          </label>
        </div>

        {listQuery.isLoading ? (
          <LoadingState />
        ) : listQuery.isError ? (
          <ErrorState
            title="纠错列表加载失败"
            description={
              listQuery.error instanceof Error ? listQuery.error.message : '接口加载失败'
            }
            retry={() => void listQuery.refetch()}
          />
        ) : rows.length === 0 ? (
          <EmptyState title="暂无纠错建议" description="员工提交的题目纠错 / 优化建议会显示在这里。" />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs text-slate-500">
                    <th className="py-2 pr-3">提交人</th>
                    <th className="py-2 pr-3">课程</th>
                    <th className="py-2 pr-3">题干</th>
                    <th className="py-2 pr-3">建议</th>
                    <th className="py-2 pr-3">状态</th>
                    <th className="py-2 pr-3">提交时间</th>
                    <th className="py-2 pr-3 text-right">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => (
                    <tr
                      key={c.id}
                      className="border-b border-slate-100 align-top"
                      data-testid={`correction-row-${c.id}`}
                    >
                      <td className="py-2 pr-3 text-slate-700">{c.employeeName ?? c.employeeId}</td>
                      <td className="py-2 pr-3 text-slate-500">{c.courseName ?? '—'}</td>
                      <td className="max-w-[220px] py-2 pr-3 text-slate-600">
                        <div className="line-clamp-2">{c.questionSnapshot}</div>
                      </td>
                      <td className="max-w-[240px] py-2 pr-3 text-slate-600">
                        <div className="line-clamp-2">{c.suggestion}</div>
                      </td>
                      <td className="py-2 pr-3">
                        <span
                          className={clsx(
                            'rounded-full px-2 py-0.5 text-[10px] font-medium',
                            STATUS_META[c.status].cls,
                          )}
                        >
                          {STATUS_META[c.status].label}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-slate-500">{formatDateTime(c.createdAt)}</td>
                      <td className="py-2 pr-3 text-right">
                        <button
                          type="button"
                          className={SECONDARY_BTN}
                          onClick={() => setSelected(c)}
                          data-testid={`correction-open-${c.id}`}
                        >
                          {c.status === 'PENDING' && canHandle ? '处理' : '查看'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination ? (
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
                <span>
                  共 {pagination.total} 条 · 第 {pagination.page}/{pagination.totalPages} 页
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className={SECONDARY_BTN}
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    上一页
                  </button>
                  <button
                    type="button"
                    className={SECONDARY_BTN}
                    disabled={page >= pagination.totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    下一页
                  </button>
                </div>
              </div>
            ) : null}
          </>
        )}
      </Card>

      {selected ? (
        <HandleModal
          key={selected.id}
          correction={selected}
          canHandle={canHandle}
          onClose={() => setSelected(null)}
          onDone={async () => {
            await refresh();
            await statsQuery.refetch();
            setSelected(null);
          }}
        />
      ) : null}
    </div>
  );
}

type HandleMode = 'accept' | 'reject' | 'ignore' | null;

function HandleModal({
  correction,
  canHandle,
  onClose,
  onDone,
}: {
  correction: CorrectionListItem;
  canHandle: boolean;
  onClose: () => void;
  onDone: () => Promise<void> | void;
}) {
  const pending = correction.status === 'PENDING';
  const [mode, setMode] = useState<HandleMode>(null);

  return (
    <Modal
      wide
      title="纠错建议详情"
      description={`提交人：${correction.employeeName ?? correction.employeeId} · ${formatDateTime(correction.createdAt)}`}
      onClose={onClose}
      footer={
        <button type="button" className={SECONDARY_BTN} onClick={onClose}>
          关闭
        </button>
      }
    >
      <div className="space-y-3 text-sm">
        <Field label="课程" value={correction.courseName ?? '—'} />
        <Field label="题干（提交时快照）" value={correction.questionSnapshot} multiline />
        <Field label="答案（提交时快照）" value={correction.answerSnapshot} multiline />
        <Field label="建议内容" value={correction.suggestion} multiline highlight />

        {!pending ? (
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            <div>
              处理结果：
              <span className="font-medium">{STATUS_META[correction.status].label}</span>
              {correction.handledByName ? ` · ${correction.handledByName}` : ''}
              {correction.handledAt ? ` · ${formatDateTime(correction.handledAt)}` : ''}
            </div>
            {correction.handleNote ? (
              <div className="mt-1">备注：{correction.handleNote}</div>
            ) : null}
          </div>
        ) : !canHandle ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            处理纠错仅 ADMIN / SUPER_ADMIN 可操作。
          </div>
        ) : mode === null ? (
          <div className="flex flex-wrap gap-2 pt-1">
            <button type="button" className={PRIMARY_BTN} onClick={() => setMode('accept')} data-testid="correction-accept-mode">
              采纳并修改
            </button>
            <button type="button" className={SECONDARY_BTN} onClick={() => setMode('reject')} data-testid="correction-reject-mode">
              驳回
            </button>
            <button type="button" className={SECONDARY_BTN} onClick={() => setMode('ignore')} data-testid="correction-ignore-mode">
              忽略
            </button>
          </div>
        ) : mode === 'accept' ? (
          <AcceptForm correction={correction} onBack={() => setMode(null)} onDone={onDone} />
        ) : (
          <RejectIgnoreForm
            correction={correction}
            mode={mode}
            onBack={() => setMode(null)}
            onDone={onDone}
          />
        )}
      </div>
    </Modal>
  );
}

function AcceptForm({
  correction,
  onBack,
  onDone,
}: {
  correction: CorrectionListItem;
  onBack: () => void;
  onDone: () => Promise<void> | void;
}) {
  // 采纳并修改从**当前**知识库条目预填（题目自提交后可能已变化）；取不到（题目已删）时回退快照。
  const entryQuery = useQuery({
    queryKey: ['admin', 'knowledge', correction.questionId],
    queryFn: () => getKnowledgeEntryApi(correction.questionId),
    retry: false,
  });
  const [initialized, setInitialized] = useState(false);
  const [question, setQuestion] = useState(correction.questionSnapshot);
  const [answer, setAnswer] = useState(correction.answerSnapshot);
  const [explanation, setExplanation] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  // 条目返回后一次性回填当前值。
  useEffect(() => {
    if (entryQuery.data && !initialized) {
      setQuestion(entryQuery.data.question ?? correction.questionSnapshot);
      setAnswer(entryQuery.data.answer ?? correction.answerSnapshot);
      setExplanation(entryQuery.data.explanation ?? '');
      setInitialized(true);
    }
  }, [entryQuery.data, initialized, correction]);

  const mutation = useMutation({
    mutationFn: () =>
      acceptCorrectionApi(correction.id, {
        question: question.trim() || undefined,
        answer: answer.trim() || undefined,
        explanation: explanation.trim() ? explanation.trim() : undefined,
        note: note.trim() || undefined,
      }),
    onSuccess: async () => {
      setError(null);
      await onDone();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : '采纳失败，请重试'),
  });

  return (
    <div className="space-y-2 rounded-md border border-emerald-200 bg-emerald-50/40 p-3">
      <div className="text-xs font-medium text-emerald-800">采纳并修改知识库</div>
      <label className="block text-xs text-slate-600">
        <span className="mb-1 block">题干</span>
        <textarea
          className={clsx(INPUT, 'w-full')}
          rows={2}
          value={question}
          maxLength={4096}
          onChange={(e) => setQuestion(e.target.value)}
        />
      </label>
      <label className="block text-xs text-slate-600">
        <span className="mb-1 block">答案（上限 20000 字）</span>
        <textarea
          className={clsx(INPUT, 'w-full')}
          rows={4}
          value={answer}
          maxLength={20000}
          onChange={(e) => setAnswer(e.target.value)}
          data-testid="correction-accept-answer"
        />
      </label>
      <label className="block text-xs text-slate-600">
        <span className="mb-1 block">解析（留空则不改动）</span>
        <textarea
          className={clsx(INPUT, 'w-full')}
          rows={2}
          value={explanation}
          maxLength={4096}
          onChange={(e) => setExplanation(e.target.value)}
        />
      </label>
      <label className="block text-xs text-slate-600">
        <span className="mb-1 block">处理备注（可选）</span>
        <input
          className={clsx(INPUT, 'w-full')}
          value={note}
          maxLength={1000}
          onChange={(e) => setNote(e.target.value)}
        />
      </label>
      {error ? (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-xs text-rose-700">
          {error}
        </div>
      ) : null}
      <div className="flex justify-end gap-2 pt-1">
        <button type="button" className={SECONDARY_BTN} onClick={onBack}>
          返回
        </button>
        <button
          type="button"
          className={PRIMARY_BTN}
          disabled={mutation.isPending || (!question.trim() && !answer.trim() && !explanation.trim())}
          onClick={() => {
            setError(null);
            mutation.mutate();
          }}
          data-testid="correction-accept-submit"
        >
          {mutation.isPending ? '提交中…' : '采纳并保存修改'}
        </button>
      </div>
    </div>
  );
}

function RejectIgnoreForm({
  correction,
  mode,
  onBack,
  onDone,
}: {
  correction: CorrectionListItem;
  mode: 'reject' | 'ignore';
  onBack: () => void;
  onDone: () => Promise<void> | void;
}) {
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const isReject = mode === 'reject';

  const mutation = useMutation({
    mutationFn: () =>
      isReject
        ? rejectCorrectionApi(correction.id, note.trim())
        : ignoreCorrectionApi(correction.id, note.trim() || undefined),
    onSuccess: async () => {
      setError(null);
      await onDone();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : '操作失败，请重试'),
  });

  const disabled = mutation.isPending || (isReject && note.trim().length === 0);

  return (
    <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3">
      <div className="text-xs font-medium text-slate-700">{isReject ? '驳回' : '忽略'}纠错建议</div>
      <label className="block text-xs text-slate-600">
        <span className="mb-1 block">{isReject ? '驳回理由（必填）' : '备注（可选）'}</span>
        <textarea
          className={clsx(INPUT, 'w-full')}
          rows={3}
          value={note}
          maxLength={1000}
          onChange={(e) => setNote(e.target.value)}
          data-testid="correction-note"
        />
      </label>
      {error ? (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-xs text-rose-700">
          {error}
        </div>
      ) : null}
      <div className="flex justify-end gap-2 pt-1">
        <button type="button" className={SECONDARY_BTN} onClick={onBack}>
          返回
        </button>
        <button
          type="button"
          className={isReject ? DANGER_BTN : PRIMARY_BTN}
          disabled={disabled}
          onClick={() => {
            setError(null);
            mutation.mutate();
          }}
          data-testid="correction-reject-ignore-submit"
        >
          {mutation.isPending ? '提交中…' : isReject ? '确认驳回' : '确认忽略'}
        </button>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  multiline,
  highlight,
}: {
  label: string;
  value: string;
  multiline?: boolean;
  highlight?: boolean;
}) {
  return (
    <div>
      <div className="text-xs font-medium text-slate-500">{label}</div>
      <div
        className={clsx(
          'mt-0.5 whitespace-pre-wrap rounded-md px-2.5 py-1.5 text-sm',
          highlight ? 'bg-amber-50 text-slate-800' : 'bg-slate-50 text-slate-700',
          multiline ? 'max-h-40 overflow-y-auto' : '',
        )}
      >
        {value}
      </div>
    </div>
  );
}
